import { useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { Upload, X, CheckCircle, AlertCircle, FileText, Image, Video, Music, Shield, Globe } from 'lucide-react'
import { formatFileSize } from '@/lib/utils'
import { uploadService, UploadOptions } from '@/services/uploadService'
import toast from 'react-hot-toast'

const ContentUpload: React.FC = () => {
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploadOptions, setUploadOptions] = useState<UploadOptions>({
    title: '',
    description: '',
    tags: [],
    category: '',
    isPublic: false
  })

  const onDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setSelectedFile(acceptedFiles[0])
    }
  }

  const { getRootProps, getInputProps, isDragActive, acceptedFiles } = useDropzone({
    onDrop,
    accept: {
      'image/*': [],
      'video/*': [],
      'audio/*': []
    },
    maxSize: 100 * 1024 * 1024, // 100MB
    multiple: false
  })

  const handleUpload = async () => {
    if (!selectedFile) return

    setUploading(true)
    setUploadProgress(0)

    try {
      await uploadService.uploadContent(
        selectedFile,
        uploadOptions,
        (progress) => setUploadProgress(progress)
      )
      
      toast.success('Content uploaded successfully!')
      setSelectedFile(null)
      setUploadOptions({
        title: '',
        description: '',
        tags: [],
        category: '',
        isPublic: false
      })
    } catch (error: any) {
      toast.error(error.message || 'Upload failed')
    } finally {
      setUploading(false)
      setUploadProgress(0)
    }
  }

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) return Image
    if (file.type.startsWith('video/')) return Video
    if (file.type.startsWith('audio/')) return Music
    return FileText
  }

  const getFileType = (file: File) => {
    if (file.type.startsWith('image/')) return 'Image'
    if (file.type.startsWith('video/')) return 'Video'
    if (file.type.startsWith('audio/')) return 'Audio'
    return 'File'
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">B2B Content Upload</h1>
        <p className="text-gray-600 mt-1">Upload content with enterprise-grade DRM protection</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Upload Area */}
        <div className="lg:col-span-2">
          <div className="bg-white p-6 rounded-lg shadow">
            {/* File Drop Zone */}
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                isDragActive
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <input {...getInputProps()} />
              <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-lg font-medium text-gray-900 mb-2">
                {isDragActive ? 'Drop files here' : 'Drag & drop files here'}
              </p>
              <p className="text-sm text-gray-500">
                or click to select files
              </p>
              <p className="text-xs text-gray-400 mt-2">
                Max size: {formatFileSize(100 * 1024 * 1024)} • Supported: Images, Videos, Audio
              </p>
            </div>

            {/* Selected File */}
            {selectedFile && (
              <div className="mt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Selected File</h3>
                <div className="flex items-center p-4 bg-gray-50 rounded-lg">
                  {(() => {
                    const Icon = getFileIcon(selectedFile)
                    return <Icon className="h-8 w-8 text-gray-400 mr-3" />
                  })()}
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                    <p className="text-xs text-gray-500">
                      {getFileType(selectedFile)} • {formatFileSize(selectedFile.size)}
                    </p>
                  </div>
                  <CheckCircle className="h-5 w-5 text-green-500" />
                </div>
              </div>
            )}

            {/* Upload Options */}
            {selectedFile && (
              <div className="mt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Upload Options</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Title
                    </label>
                    <input
                      type="text"
                      value={uploadOptions.title}
                      onChange={(e) => setUploadOptions(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter title"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category
                    </label>
                    <select
                      value={uploadOptions.category}
                      onChange={(e) => setUploadOptions(prev => ({ ...prev, category: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select category</option>
                      <option value="music">Music</option>
                      <option value="video">Video</option>
                      <option value="image">Image</option>
                      <option value="document">Document</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={uploadOptions.description}
                      onChange={(e) => setUploadOptions(prev => ({ ...prev, description: e.target.value }))}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter description"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tags
                    </label>
                    <input
                      type="text"
                      value={uploadOptions.tags.join(', ')}
                      onChange={(e) => setUploadOptions(prev => ({ 
                        ...prev, 
                        tags: e.target.value.split(',').map(tag => tag.trim()).filter(Boolean)
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter tags separated by commas"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={uploadOptions.isPublic}
                        onChange={(e) => setUploadOptions(prev => ({ ...prev, isPublic: e.target.checked }))}
                        className="mr-2"
                      />
                      <span className="text-sm font-medium text-gray-700">Make public</span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Upload Button */}
            {selectedFile && (
              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Uploading... {uploadProgress}%
                    </>
                  ) : (
                    'Upload Content'
                  )}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* DRM Features Info */}
        <div className="lg:col-span-1">
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-medium text-gray-900 mb-4">DRM Protection Features</h3>
            <div className="space-y-4">
              <div className="flex items-start">
                <Shield className="h-5 w-5 text-blue-600 mr-3 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900">SHA-256 Fingerprinting</p>
                  <p className="text-xs text-gray-500">Unique content identification</p>
                </div>
              </div>
              <div className="flex items-start">
                <Globe className="h-5 w-5 text-green-600 mr-3 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900">IPFS Storage</p>
                  <p className="text-xs text-gray-500">Decentralized content storage</p>
                </div>
              </div>
              <div className="flex items-start">
                <FileText className="h-5 w-5 text-purple-600 mr-3 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Steganographic Watermarking</p>
                  <p className="text-xs text-gray-500">Invisible content protection</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ContentUpload 